import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from './cn';

// "1e Precisión" with the Tallerza brand: flat surfaces, 1px borders, pill
// buttons and one strong magenta action per view.
// Press feedback is a tiny scale (not a color flash) so it reads as physical.
export const estiloBoton = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap select-none',
    'font-sans text-[15px] leading-none',
    'transition-[background-color,border-color,color,box-shadow,transform,opacity] duration-150 ease-out',
    'active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-sel',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variante: {
        primario:
          'bg-primary text-on-primary font-bold shadow-[0_1px_0_rgb(0_0_0/0.08),inset_0_1px_0_rgb(255_255_255/0.12)] hover:bg-[color-mix(in_oklab,var(--tm-primary)_88%,black)]',
        secundario:
          'border border-border-input bg-surface text-text hover:bg-surface-muted hover:border-[color-mix(in_oklab,var(--tm-border-input)_70%,var(--tm-text))]',
        fantasma: 'text-text-long hover:bg-surface-muted hover:text-text',
        peligro:
          'bg-que-strong text-white font-bold hover:bg-[color-mix(in_oklab,var(--tm-que-strong)_88%,black)]',
        suave: 'bg-primary-soft text-primary-text font-bold hover:brightness-[0.97]',
        enlace:
          'h-auto! px-0! text-primary-text underline-offset-4 hover:underline active:scale-100',
      },
      tamano: {
        sm: 'h-9 rounded-full px-3.5 text-[14px] [&_svg]:size-4',
        md: 'h-[42px] rounded-full px-[18px] [&_svg]:size-[18px]',
        lg: 'h-[54px] rounded-full px-6 text-base [&_svg]:size-5',
        icono: 'size-[42px] rounded-full [&_svg]:size-[18px]',
        iconoSm: 'size-9 rounded-full [&_svg]:size-4',
      },
    },
    defaultVariants: { variante: 'secundario', tamano: 'md' },
  },
);

export type PropsBoton = ComponentProps<'button'> &
  VariantProps<typeof estiloBoton> & { asChild?: boolean; cargando?: boolean };

export function Boton({
  className,
  variante,
  tamano,
  asChild,
  cargando,
  children,
  disabled,
  ...props
}: PropsBoton) {
  const Comp = asChild ? Slot.Root : 'button';
  return (
    <Comp
      className={cn(estiloBoton({ variante, tamano }), className)}
      disabled={disabled || cargando}
      aria-busy={cargando || undefined}
      {...(asChild ? {} : { type: props.type ?? 'button' })}
      {...props}
    >
      {cargando ? (
        <>
          <span className="invisible contents">{children}</span>
          <span className="absolute inset-0 grid place-items-center">
            <Girador />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export function Girador({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={cn('size-[18px] animate-spin', className)}
      aria-hidden="true"
      fill="none"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
